import React, { useState, useEffect } from 'react';
import bugtracker_1 from '../assets/bugtracker_1.jpg';
import bugtracker_2 from '../assets/bugtracker_2.jpg';
import bugtracker_3 from '../assets/bugtracker_3.jpg';
import forgery_1 from '../assets/forgery_1.jpg';
import forgery_2 from '../assets/forgery_2.jpg';
import forgery_3 from '../assets/forgery_3.jpg';
import codereview_1 from '../assets/codereview_1.jpg';
import codereview_2 from '../assets/codereview_2.jpg';
import codereview_3 from '../assets/codereview_3.jpg';
import careeros_1 from '../assets/careeros_1.png';


const CDN = 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons';

const STACK_LOGOS = {
  'Java':         { src: `${CDN}/java/java-original.svg` },
  'Spring Boot':  { src: `${CDN}/spring/spring-original.svg` },
  'MySQL':        { src: `${CDN}/mysql/mysql-original.svg` },
  'React':        { src: `${CDN}/react/react-original.svg` },
  'JWT':          { fallback: { bg: '#000', text: 'JWT', color: '#fb015b' } },
  'Spring Security': { src: `${CDN}/spring/spring-original.svg` },
  'OpenAI API':   { fallback: { bg: '#10a37f', text: 'AI', color: '#fff' } },
  'Groq API':     { fallback: { bg: '#111', text: 'GQ', color: '#fff' } },
  'Python':       { src: `${CDN}/python/python-original.svg` },
  'TensorFlow':   { src: `${CDN}/tensorflow/tensorflow-original.svg` },
  'OpenCV':       { src: `${CDN}/opencv/opencv-original.svg` },
  'CNN':          { fallback: { bg: '#5c3ee8', text: 'CNN', color: '#fff' } },
  'Flask':        { src: `${CDN}/flask/flask-original.svg`, invert: true },
  'Node.js':      { src: `${CDN}/nodejs/nodejs-original.svg` },
  'Express':      { src: `${CDN}/express/express-original.svg`, invert: true },
  'TypeScript':   { src: `${CDN}/typescript/typescript-original.svg` },
  'Tailwind CSS': { src: `${CDN}/tailwindcss/tailwindcss-original.svg` },
  'AST Parsing':  { fallback: { bg: '#222', text: 'AST', color: '#aaa' } },
  'Docker':       { src: `${CDN}/docker/docker-original.svg` },
  'Next.js':    { src: `${CDN}/nextjs/nextjs-original.svg`, invert: true },
  'LangGraph':  { fallback: { bg: '#1C3C3C', text: 'LG',  color: '#00D4AA' } },
  'FastAPI':    { fallback: { bg: '#009688', text: 'FA',  color: '#fff' } },
  'Qdrant':     { fallback: { bg: '#DC244C', text: 'QD',  color: '#fff' } },
  'PostgreSQL': { src: `${CDN}/postgresql/postgresql-original.svg` },
  // 'Groq API':   { fallback: { bg: '#111',    text: 'GQ',  color: '#fff' } },
};

function StackTag({ name }) {
  const [err, setErr] = React.useState(false);
  const item = STACK_LOGOS[name] || { fallback: { bg: '#333', text: name.slice(0,2).toUpperCase(), color: '#fff' } };
  const showFallback = item.fallback || err;

  return (
    <span className="ptag" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {showFallback ? (
        <svg width="12" height="12" viewBox="0 0 20 20" style={{ flexShrink: 0 }}>
          <rect width="20" height="20" rx="3" fill={item.fallback?.bg || '#333'} />
          <text x="10" y="10" textAnchor="middle" dominantBaseline="central"
            fontSize={item.fallback?.text?.length > 2 ? 5.5 : 8}
            fontWeight="700" fill={item.fallback?.color || '#fff'}
            fontFamily="monospace">{item.fallback?.text || name.slice(0,2)}</text>
        </svg>
      ) : (
        <img src={item.src} width="12" height="12" alt={name}
          onError={() => setErr(true)}
          style={{ filter: item.invert ? 'invert(0.8)' : 'none', display: 'block', flexShrink: 0 }} />
      )}
      {name}
    </span>
  );
}

const FEATURED = [
  {
    id: 'careeros',
    name: 'CareerOS — AI Career OS',
    isFeatured: true,
    badge: 'Open Source · 7 Contributors · Phase 1 Active',
    num: '01 / Featured',
    stats: [
      { val: '7',   label: 'Contributors' },
      { val: '7',   label: 'Modules' },
      { val: '500', label: 'Beta Target' },
      { val: 'P1',  label: 'Phase Active' },
    ],
    bullets: [
      'Open-source AI career OS — Resume Analyzer (ATS scoring, keyword gaps), Recruiter Simulator (shortlist probability), AI Interview Coach, Skill Gap Analyzer, Learning Roadmap, Job Tracker, LinkedIn Optimizer.',
      'Architected 6 Spring Boot microservices with Spring Cloud Gateway, RabbitMQ async AI queue, Redis caching, Apache Tika resume parsing — 5 LangGraph stateful agents via FastAPI + Qdrant vector search.',
      'Leading 7 open-source contributors via structured GitHub issues, PR reviews, and CONTRIBUTING.md workflow — targeting 500 beta users at launch.',
    ],
    stack: ['Next.js', 'Spring Boot', 'LangGraph', 'FastAPI', 'Qdrant', 'PostgreSQL', 'Docker'],
    images: [careeros_1, careeros_1, careeros_1],
    links: [
      { label: 'GitHub Org', url: 'https://github.com/Careers-Os' },
      { label: 'careeros-api', url: 'https://github.com/Careers-Os/careeros-api' },
    ],
  },
  {
    id: 'bug-tracker',
    name: 'AI-Powered Bug Tracker',
    isFeatured: false,
    badge: 'Live in Production',
    num: '02',
    stats: [
      { val: '15+', label: 'REST APIs' },
      { val: '25%', label: 'DB Faster' },
      { val: 'Live', label: 'Production' },
    ],
    bullets: [
      'Multi-tenant SaaS with RBAC (Admin, Developer, Tester), full bug lifecycle, activity logs, pagination — JWT + Spring Security.',
      'Groq API (LLaMA 3.3 70B): auto-suggests severity, detects semantic duplicates, generates dev-ready reproduction steps in under 5 seconds.',
      'Clean architecture: Controller–Service–Repository + DTO, @Transactional service layer.',
    ],
    stack: ['Java', 'Spring Boot', 'MySQL', 'React', 'JWT', 'Spring Security', 'Groq API'],
    images: [bugtracker_1, bugtracker_2, bugtracker_3],
    links: [
      { label: 'Live Demo', url: 'https://bugtrackerai.netlify.app' },
      { label: 'API on Render', url: 'https://bugtracker-ai.onrender.com' },
    ],
  },
  {
    id: 'ai-forgery',
    name: 'AI Image Forgery Detection',
    isFeatured: false,
    badge: 'Govt. of India · L-151429/2024',
    num: '03',
    stats: [
      { val: '3',   label: 'Papers' },
      { val: 'GOI', label: 'Copyright' },
    ],
    bullets: [
      'CNN model: ELA + JPEG artifact detection — real-time REST API with confidence scoring & heatmap visualization.',
      'Government of India copyright holder — 3 published peer-reviewed research papers.',
    ],
    stack: ['Python', 'TensorFlow', 'OpenCV', 'CNN', 'Flask'],
    images: [forgery_1, forgery_2, forgery_3],
    links: [
      { label: 'GitHub Repo', url: 'https://github.com/vikash1311/Image-Forgery-Detection' },
      { label: 'Copyright', url: 'https://drive.google.com/file/d/1kqD7doPYv-i6OHx0abJyu_fPluiTmfIr/view' },
    ],
  },
  {
    id: 'ai-code-review',
    name: 'AI Code Review Assistant',
    isFeatured: false,
    badge: 'Open Source · Live',
    num: '04',
    stats: [
      { val: 'AST',  label: 'Pre-process' },
      { val: 'Live', label: 'Demo' },
    ],
    bullets: [
      'Accepts code snippets or GitHub URLs — AI review: bugs, security vulnerabilities, cyclomatic complexity via acorn AST, refactoring suggestions.',
      'Chose Groq over OpenAI: 14,400 free req/day, same SDK — stateless backend, no DB, faster response.',
    ],
    stack: ['Node.js', 'React', 'Groq API', 'AST Parsing', 'TypeScript'],
    images: [codereview_1, codereview_2, codereview_3],
    links: [
      { label: 'Live Demo', url: 'https://code-tester-ai.netlify.app' },
      { label: 'API on Render', url: 'https://ai-code-review-kukl.onrender.com' },
    ],
  },
];

function Slideshow({ images, onZoom }) {
  const [index, setIndex] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => { setIndex(p => (p + 1) % images.length); setFading(false); }, 300);
    }, 3400);
    return () => clearInterval(t);
  }, [images.length]);

  const goTo = i => {
    if (i === index) return;
    setFading(true);
    setTimeout(() => { setIndex(i); setFading(false); }, 260);
  };

  return (
    <div
      style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', cursor: 'zoom-in' }}
      onClick={() => onZoom(images[index])}
    >
      <div className="pgbg" />

      {images.map((img, i) => (
        <div key={i} style={{
          position: 'absolute', inset: 0,
          opacity: i === index ? (fading ? 0 : 1) : 0,
          transition: 'opacity 0.35s ease',
          zIndex: i === index ? 2 : 1,
        }}>
          <img
            src={img}
            alt={`Screenshot ${i + 1}`}
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'top center',
              transform: i === index && !fading ? 'scale(1.025)' : 'scale(1)',
              transition: 'transform 4s ease',
            }}
          />
        </div>
      ))}

      {/* Gradient overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(5,7,12,0.5) 0%, rgba(5,7,12,0.08) 40%, transparent 70%)',
        zIndex: 3, pointerEvents: 'none',
      }} />

      {/* Zoom hint */}
      <div style={{
        position: 'absolute', top: 11, right: 11, zIndex: 4,
        background: 'rgba(5,7,12,0.6)', backdropFilter: 'blur(6px)',
        border: '1px solid var(--border-color)', borderRadius: 3,
        padding: '3px 8px',
        fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '1.5px',
        color: 'var(--accent-color)', textTransform: 'uppercase', pointerEvents: 'none',
      }}>ZOOM</div>

      {/* Frame counter */}
      <div style={{
        position: 'absolute', bottom: 13, left: 14, zIndex: 4,
        fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '1.5px',
        color: 'rgba(255,255,255,0.4)', pointerEvents: 'none',
      }}>{String(index + 1).padStart(2,'0')} / {String(images.length).padStart(2,'0')}</div>

      {/* Dot nav */}
      <div
        style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6, zIndex: 4 }}
        onClick={e => e.stopPropagation()}
      >
        {images.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} style={{
            width: i === index ? 18 : 6, height: 6, borderRadius: 3,
            background: i === index ? 'var(--accent-color)' : 'rgba(255,255,255,0.3)',
            border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.3s ease',
          }} />
        ))}
      </div>
    </div>
  );
}

function StatChip({ val, label }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '6px 13px', borderRadius: 4,
      background: 'var(--accent-glow)', border: '1px solid var(--border-color)',
      minWidth: 48,
    }}>
      <span style={{ fontFamily: 'var(--bebas)', fontSize: 17, letterSpacing: 1, color: 'var(--accent-color)', lineHeight: 1 }}>{val}</span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: 0.5, color: 'var(--text-muted)', marginTop: 3, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

function ProjectCard({ project, onZoom }) {
  const isFeatured = project.isFeatured;
  return (
    <div className={`pc ${isFeatured ? 'feat' : ''}`}>
      {/* Accent top stripe */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: 'linear-gradient(90deg, var(--accent-color), var(--accent2-color), transparent)',
        zIndex: 5,
      }} />

      <div className="pvis">
        <Slideshow images={project.images} onZoom={onZoom} />
      </div>

      <div className="pbody">
        {project.badge && (
          <div className="pcb">
            <span className="av-dot" style={{ width: 4, height: 4, background: 'var(--accent2-color)' }} />
            {project.badge}
          </div>
        )}

        <div className="pnum">{project.num}</div>
        <div className="pname">{project.name}</div>

        {/* Stats row- all cards */}
        <div style={{ display: 'flex', gap: 7, marginBottom: 16, flexWrap: 'wrap' }}>
          {project.stats.map((s, i) => <StatChip key={i} val={s.val} label={s.label} />)}
        </div>

        <ul className="pbl">
          {project.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>

<div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 18 }}>
          {project.stack.map(t => <StackTag key={t} name={t} />)}
        </div>

        <div className="plinks">
          {project.links.map(l => (
            <a key={l.label} href={l.url} target="_blank" rel="noreferrer" className="plnk">{l.label}</a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const [zoomImg, setZoomImg] = useState(null);

  useEffect(() => {
    document.body.style.overflow = zoomImg ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [zoomImg]);

  return (
    <section id="projects" style={{ background: 'transparent' }}>
      <div className="inn">
        <div className="shdr reveal">
          <div className="sn2">// selected_work</div>
          <h2 className="st">SELECTED<br />WORK</h2>
          <div className="sl2" />
        </div>

        <div className="pgrid reveal">
          {FEATURED.map(p => (
            <ProjectCard key={p.id} project={p} onZoom={setZoomImg} />
          ))}
        </div>

        {/* View all on GitHub */}
        {/* View all on GitHub */}
        <div className="reveal" style={{ display: 'flex', justifyContent: 'center', marginTop: '3rem' }}>
          <a
            href="https://github.com/vikash1311"
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '2px',
              textTransform: 'uppercase', textDecoration: 'none',
              color: 'var(--accent-color)',
              border: '1px solid var(--accent-color)',
              padding: '12px 28px', borderRadius: 3,
              transition: 'all 0.22s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--accent-color)';
              e.currentTarget.style.color = 'var(--bg)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--accent-color)';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22"/>
            </svg>
            View all projects on GitHub {'\u2197'}
          </a>
        </div>
      </div>


      {/* Lightbox */}
      {zoomImg && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(5,7,12,0.96)', backdropFilter: 'blur(14px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            animation: 'fadeUp 0.25s ease',
          }}
          onClick={() => setZoomImg(null)}
        >
          <button
            onClick={() => setZoomImg(null)}
            style={{
              position: 'absolute', top: 28, right: 36,
              background: 'transparent', border: '1px solid var(--border-color)',
              color: 'var(--accent-color)', width: 40, height: 40, borderRadius: 4,
              fontFamily: 'var(--mono)', fontSize: 20, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-color)'; e.currentTarget.style.background = 'var(--accent-glow)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.background = 'transparent'; }}
          >×</button>

          <span style={{
            position: 'absolute', bottom: 30,
            fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '2.5px',
            color: 'var(--text-muted)', textTransform: 'uppercase',
          }}>Click anywhere to close</span>

          <div
            style={{
              maxWidth: '84vw', maxHeight: '80vh',
              border: '1px solid var(--border-color)', borderRadius: 12,
              background: 'rgba(0,0,0,0.5)', padding: 10,
              boxShadow: '0 32px 80px rgba(0,0,0,0.8)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src={zoomImg}
              alt="Enlarged screenshot"
              style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8, display: 'block' }}
            />
          </div>
        </div>
      )}
    </section>
  );
}
